"use client";

import { useRef, useState, useEffect, useCallback } from "react";
import { ArrowRight, BookOpen, UserCheck, X } from 'lucide-react';
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP);
export default function Foundation() {
  const container = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add({
      reduce: "(prefers-reduced-motion: reduce)",
      motion: "(prefers-reduced-motion: no-preference)"
    }, context => {
      const conditions = context.conditions as {
        reduce: boolean;
        motion: boolean;
      };
      if (conditions.reduce) {
        gsap.set([".headline-line", ".lead-copy", ".cta-row", ".hero-photo", ".floating-card", ".gold-underline"], {
          opacity: 1,
          y: 0,
          scale: 1,
          scaleX: 1
        });
        return;
      }
      const tl = gsap.timeline({
        defaults: {
          ease: "power3.out"
        }
      });
      tl.from(".headline-line", {
        yPercent: 100,
        opacity: 0,
        duration: 0.7,
        stagger: 0.08
      }).from(".gold-underline", {
        scaleX: 0,
        transformOrigin: "left center",
        duration: 0.6,
        ease: "power2.out"
      }, "-=0.2").from(".lead-copy", {
        opacity: 0,
        y: 20,
        duration: 0.6
      }, "-=0.3").from(".cta-row", {
        opacity: 0,
        y: 16,
        duration: 0.5
      }, "-=0.3").from(".hero-photo", {
        opacity: 0,
        scale: 0.98,
        duration: 0.8,
        ease: "power2.out"
      }, 0.2).from(".floating-card", {
        opacity: 0,
        y: 16,
        duration: 0.5
      }, "-=0.1");
    });
    return () => mm.revert();
  }, {
    scope: container
  });
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    triggerRef.current?.focus();
  }, []);
  useEffect(() => {
    if (!isModalOpen) return;
    closeButtonRef.current?.focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCloseModal();
        return;
      }
      if (e.key === "Tab" && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpen, handleCloseModal]);
  return <section data-section-id="24083" id="victoria-light-foundation" className="bg-background text-foreground overflow-hidden">
      <div ref={container} className="mx-auto max-w-[1240px] px-5 md:px-10 lg:px-16 py-32 md:py-44 lg:py-56">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 flex flex-col">
            <div>
              <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary rounded-lg px-4 py-1.5 text-xs font-semibold tracking-widest uppercase inline-flex items-center gap-2">
                <BookOpen className="size-3" aria-hidden="true" />
                <span>Membership &middot; Open Now</span>
              </Badge>
            </div>

            <h1 className="mt-8 font-default font-black text-foreground leading-[0.92] tracking-tight text-[clamp(3.25rem,8.5vw,7.5rem)]">
              <span className="block overflow-hidden">
                <span className="headline-line block text-[clamp(2rem,5vw,4.5rem)]" style={{
                translate: 'none',
                rotate: 'none',
                scale: 'none',
                opacity: '1',
                transform: 'translate(0px, 0px)'
              }}>
                  The Victoria Foundation of Love and Light.
                </span>
              </span>
            </h1>

            <p className="lead-copy mt-8 max-w-xl text-lg md:text-xl leading-relaxed text-muted-foreground">
              Join our community dedicated to making the world a better place. 
              Register for our 3rd quarter membership to access exclusive 
              resources and support our mission.
            </p>

            <div className="cta-row mt-10 flex flex-wrap items-center gap-4 sm:gap-6">
              <Button asChild className="group h-auto rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-7 py-4 text-sm font-semibold shadow-sm">
                <Link to="/QuarterMembershipPage">
                  <span>Register now</span>
                  <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="hero-photo relative aspect-[4/5] w-full overflow-hidden rounded-3xl bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1744672/img/foundation_community_support.png" alt="Community members working together in a bright, welcoming space" loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </div>

            <div className="floating-card absolute -top-4 -left-4 md:-top-6 md:-left-8 max-w-[15rem] rounded-2xl border border-border bg-card text-card-foreground p-4 shadow-lg">
              <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-muted-foreground">
                Registration
              </div>
              <div className="mt-1.5 text-base font-semibold text-foreground leading-snug">4th Quarter</div>
              <div className="mt-3 flex items-center gap-1.5">
                <span className="h-1 flex-1 rounded-lg bg-secondary" />
                <span className="h-1 flex-1 rounded-lg bg-border" />
                <span className="h-1 flex-1 rounded-lg bg-border" />
                <span className="h-1 flex-1 rounded-lg bg-border" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-foreground/40 backdrop-blur-xs transition-opacity duration-200" onClick={handleCloseModal} role="presentation">
          <div ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="model-modal-title" aria-describedby="model-modal-description" className="relative w-full max-w-lg rounded-2xl border border-border bg-card p-6 sm:p-8 text-card-foreground shadow-lg transition-all" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 pb-4 border-b border-border">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-lg bg-secondary/20 text-secondary-foreground">
                  <UserCheck className="size-5" aria-hidden="true" />
                </div>
                <div>
                  <h2 id="model-modal-title" className="text-lg font-bold tracking-tight text-foreground">
                    Model Management
                  </h2>
                  <p className="text-xs text-muted-foreground">Official Foundation Notice</p>
                </div>
              </div>
              <Button ref={closeButtonRef} type="button" variant="ghost" size="icon" onClick={handleCloseModal} aria-label="Close dialog" className="rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted">
                <X className="size-5" aria-hidden="true" />
              </Button>
            </div>

            <div className="py-6">
              <p id="model-modal-description" className="text-sm sm:text-base leading-relaxed text-foreground/90 font-normal">
                Dear Samson Jones, Kindly inform your candidate &apos;Alona Grigg&apos; that the minimum foundation donation for all Victoria&apos;s Secret models has been updated from $100 to $150. To be paid on or before the 30th of August, 2026.
              </p>
            </div>

            <div className="flex justify-end pt-4 border-t border-border">
              <Button type="button" onClick={handleCloseModal} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-2.5 text-sm font-semibold">
                Acknowledge &amp; Close
              </Button>
            </div>
          </div>
        </div>}
    </section>;
}